import message from "@/utils/globalMessage";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, Button, Spin, Card, Space, Row, Col, Switch, Tag, Tooltip, InputNumber, Checkbox, Typography } from "antd";
import { SaveOutlined, ArrowLeftOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { EventName, emitEvent } from "@/hooks/useEventBus";
import CustomDatePicker from "@components/CustomDatePicker";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import {
  deriveMonthlyPattern,
  dayOfMonthFromStartDate,
  buildPMPlanSchedulePayload,
  WEEKDAY_OPTIONS,
  MONTHLY_ORDINAL_OPTIONS,
} from "../pmScheduleUtils";
import Select from "@components/ResponsiveSelect";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const HOLIDAY_COUNTRY_OPTIONS = [
  { value: "", label: "Weekends only (no public holidays)" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
  { value: "CN", label: "China" },
  { value: "MX", label: "Mexico" },
];

const MONTHLY_PATTERN_OPTIONS = [
  { value: "day_of_month", label: "Same calendar day each month (e.g. the 22nd)" },
  { value: "nth_weekday", label: "Same weekday slot each month (e.g. 4th Wednesday)" },
];

const CYCLE_TYPES = [
  { value: "monthly", label: "Monthly" },
  { value: "every_n_days", label: "Every N Days" },
  { value: "every_n_hours", label: "Every N Operating Hours" },
];

const PMPlanDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshTab } = useKeepAliveContext();
  const [form] = Form.useForm();
  const facilityId = useSelector((state) => state.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const listRoute = buildFacilityPath("pm-plan", facilityContext) || "/switch-view";

  const [loading, setLoading] = useState(!isAdd);
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(!isAdd);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [cycleType, setCycleType] = useState(null);

  /* Equipment search */
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [equipmentSearchLoading, setEquipmentSearchLoading] = useState(false);

  /* PM Checklist options for the selected category */
  const [checklistOptions, setChecklistOptions] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);

  /**
   * selectedCategoryId  – FK id, used for PMChecklist filter (uses equipment_category FK)
   * selectedCategoryName – text name, used for Equipment filter (uses legacy `category` text field)
   */
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState(null);

  const [triggering, setTriggering] = useState(false);

  /* Keep refs so async callbacks always read the latest values without stale closures */
  const categoryIdRef = useRef(null);
  const categoryNameRef = useRef(null);
  useEffect(() => {
    categoryIdRef.current = selectedCategoryId;
  }, [selectedCategoryId]);
  useEffect(() => {
    categoryNameRef.current = selectedCategoryName;
  }, [selectedCategoryName]);

  /* ── Data loaders ─────────────────────────────────────────────────────── */

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.filterEquipmentCategory("page=1&page_size=999");
      if (res.status === 200) setCategoryOptions(res.data.results || []);
    } catch {
      setCategoryOptions([]);
    }
  }, []);

  /**
   * Fetch equipment filtered by facilityId AND (optionally) a category.
   *
   * NOTE: existing equipment records use the LEGACY text field `category` (e.g. "Air handling unit"),
   * NOT the new FK field `equipment_category`. We therefore filter by `category=<name>` when a
   * category is selected. The new `equipment_category` FK field is only used for PMChecklist
   * matching (where the data IS properly migrated).
   *
   * Both keyword and categoryName are passed explicitly – no async form-reading.
   */
  const fetchEquipments = useCallback(
    async (keyword = "", categoryName = null) => {
      try {
        setEquipmentSearchLoading(true);
        const parts = [];
        if (keyword) parts.push(`search=${encodeURIComponent(keyword)}`);
        if (facilityId) parts.push(`facility=${facilityId}`);
        // Use the legacy text `category` field – this is what the existing equipment data uses
        if (categoryName) parts.push(`category=${encodeURIComponent(categoryName)}`);
        parts.push("page_size=100");
        const res = await api.filterEquipment(parts.join("&"));
        if (res.status === 200) {
          setEquipmentOptions(res.data.results || []);
        }
      } catch { /* ignore */ } finally {
        setEquipmentSearchLoading(false);
      }
    },
    [facilityId]
  );

  const loadChecklistsForCategory = useCallback(async (categoryId) => {
    if (categoryId == null) {
      setChecklistOptions([]);
      return;
    }
    try {
      setChecklistLoading(true);
      const res = await api.filterPMChecklist(
        `equipment_category=${categoryId}&page_size=999`
      );
      if (res.status === 200) setChecklistOptions(res.data.results || []);
      else setChecklistOptions([]);
    } catch {
      setChecklistOptions([]);
    } finally {
      setChecklistLoading(false);
    }
  }, []);

  /* Debounced wrapper for keyword search – reads categoryName from ref (always fresh) */
  const debouncedSearch = useMemo(() => {
    let timer = null;
    return (keyword) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fetchEquipments(keyword, categoryNameRef.current);
      }, 400);
    };
  }, [fetchEquipments]);

  /* ── Lifecycle ────────────────────────────────────────────────────────── */

  useEffect(() => {
    fetchCategories();
    if (!isAdd && id) {
      fetchDetail();
    } else {
      fetchEquipments("", null);
      setCycleType(null);
    }
  }, [id, isAdd]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Re-fetch equipment when facilityId becomes available (Redux async) */
  useEffect(() => {
    if (facilityId) {
      fetchEquipments("", categoryNameRef.current);
    }
  }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Detail fetch ─────────────────────────────────────────────────────── */

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await api.getPMPlanDetail(id);
      if (res.status === 200) {
        const data = res.data;

        /* Recover category id+name from the saved checklist's category */
        const catId = data.pm_checklist_detail?.equipment_category ?? null;
        const catName = data.pm_checklist_detail?.equipment_category_name ?? null;

        form.setFieldsValue({
          name: data.name,
          description: data.description,
          cycle_type: data.cycle_type,
          cycle_value: data.cycle_value,
          monthly_interval: data.monthly_interval ?? 1,
          monthly_pattern: deriveMonthlyPattern(data),
          monthly_day_of_month: data.monthly_day_of_month,
          monthly_week_ordinal: data.monthly_week_ordinal,
          monthly_weekday: data.monthly_weekday,
          start_date: data.start_date,
          end_date: data.end_date,
          next_schedule_date: data.next_schedule_date,
          skip_non_business_days: data.skip_non_business_days ?? false,
          holiday_country: data.holiday_country ?? "",
          cost_budget: data.cost_budget,
          estimate_total_hours: data.estimate_total_hours,
          is_active: data.is_active,
          equipments: data.equipments,
          pm_checklist: data.pm_checklist ?? undefined,
          equipment_category_filter: catId,
        });
        setCycleType(data.cycle_type);
        setSelectedEquipments(data.equipments || []);

        /* Set category state + refs before async calls so they use the right value */
        setSelectedCategoryId(catId);
        setSelectedCategoryName(catName);
        categoryIdRef.current = catId;
        categoryNameRef.current = catName;

        await Promise.all([
          loadChecklistsForCategory(catId),
          fetchEquipments("", catName).then(() => {
            /* Also ensure already-selected equipment appear in the dropdown */
            const eqNames = data.equipment_names || [];
            const eqIds = data.equipments || [];
            const preloaded = eqIds.map((eid, idx) => ({
              id: eid,
              name: eqNames[idx] || `Equipment #${eid}`,
            }));
            setEquipmentOptions((prev) => {
              const existingIds = new Set(prev.map((e) => e.id));
              const merged = [...prev];
              preloaded.forEach((eq) => {
                if (!existingIds.has(eq.id)) merged.push(eq);
              });
              return merged;
            });
          }),
        ]);
      } else {
        message.error("PM Plan not found");
      }
    } catch {
      message.error("Failed to load PM Plan");
    } finally {
      setLoading(false);
    }
  };

  /* ── Event handlers ───────────────────────────────────────────────────── */

  const handleEquipmentCategoryChange = useCallback(
    (catId) => {
      /* catId is the raw value from the Select (number or undefined) */
      const normalisedId = catId ?? null;
      /* Resolve the human-readable name from categoryOptions for equipment text-field filter */
      const catObj = normalisedId != null
        ? categoryOptions.find((c) => c.id === normalisedId) ?? null
        : null;
      const normalisedName = catObj?.name ?? null;

      setSelectedCategoryId(normalisedId);
      setSelectedCategoryName(normalisedName);
      categoryIdRef.current = normalisedId;
      categoryNameRef.current = normalisedName;

      /* Reset dependent fields */
      form.setFieldsValue({ equipments: [], pm_checklist: undefined });
      setSelectedEquipments([]);
      setEquipmentOptions([]);

      loadChecklistsForCategory(normalisedId);
      fetchEquipments("", normalisedName);
    },
    [form, categoryOptions, loadChecklistsForCategory, fetchEquipments]
  );

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const {
        equipment_category_filter,
        monthly_pattern,
        next_schedule_date,
        ...planFields
      } = values;
      void equipment_category_filter;
      void monthly_pattern;
      void next_schedule_date;
      setSaving(true);
      const schedulePart = buildPMPlanSchedulePayload(values);
      const body = {
        name: planFields.name,
        description: planFields.description,
        ...schedulePart,
        cost_budget: planFields.cost_budget,
        estimate_total_hours: planFields.estimate_total_hours,
        is_active: planFields.is_active,
        equipments: planFields.equipments,
        pm_checklist: planFields.pm_checklist != null ? planFields.pm_checklist : null,
        facility: facilityId,
      };

      let res;
      if (isAdd) {
        res = await api.createPMPlan(body);
      } else {
        res = await api.editPMPlan(id, body);
      }
      if (res.status === 200 || res.status === 201) {
        message.success(isAdd ? "PM Plan created" : "PM Plan updated");
        emitEvent(EventName.REFRESH_PM_PLAN_LIST);
        refreshTab(listRoute);
        if (isAdd) navigate(buildFacilityPath(`pm-plan-detail/${res.data.id}`, facilityContext) || listRoute);
        else { setReadOnly(true); fetchDetail(); }
      } else {
        message.error("Save failed");
      }
    } catch (err) {
      if (err.errorFields) return;
      message.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTrigger = async () => {
    try {
      setTriggering(true);
      const res = await api.triggerPMPlan(id);
      if (res.status === 201) {
        message.success(`PM Plan triggered! Workorder Wrapper #${res.data.id} created.`);
      }
    } catch {
      message.error("Failed to trigger PM Plan");
    } finally {
      setTriggering(false);
    }
  };

  /* ── Render ───────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const noCategorySelected = selectedCategoryId == null;
  const checklistDisabledInEdit = (!readOnly || isAdd) && noCategorySelected;

  return (
    <div style={{ padding: "16px" }}>
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => { refreshTab(listRoute); navigate(listRoute); }}
            />
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {isAdd ? "Create PM Plan" : "PM Plan Detail"}
            </span>
          </Space>
        }
        extra={
          <Space>
            {!isAdd && (
              <Tooltip title="Manually trigger this plan to generate workorders now">
                <Button
                  icon={<ThunderboltOutlined />}
                  loading={triggering}
                  onClick={handleTrigger}
                  style={{ color: "#f59e0b", borderColor: "#f59e0b" }}
                >
                  Trigger Now
                </Button>
              </Tooltip>
            )}
            {!isAdd && readOnly && <Button onClick={() => setReadOnly(false)}>Edit</Button>}
            {(!readOnly || isAdd) && (
              <>
                {!isAdd && (
                  <Button onClick={() => { setReadOnly(true); fetchDetail(); }}>Cancel</Button>
                )}
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                  Save
                </Button>
              </>
            )}
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          disabled={readOnly && !isAdd}
          initialValues={{
            monthly_interval: 1,
            monthly_pattern: "day_of_month",
            skip_non_business_days: false,
            is_active: true,
          }}
          onValuesChange={(changed, all) => {
            if (
              !isAdd ||
              all.cycle_type !== "monthly" ||
              all.monthly_pattern !== "day_of_month" ||
              !all.start_date
            ) {
              return;
            }
            if (changed.start_date != null || changed.monthly_pattern != null || changed.cycle_type != null) {
              const dom = dayOfMonthFromStartDate(String(all.start_date));
              if (dom != null) form.setFieldsValue({ monthly_day_of_month: dom });
            }
          }}
        >
          <Row gutter={16}>

            {/* ── Basic info ── */}
            <Col span={16}>
              <Form.Item label="Plan Name" name="name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Chiller Annual PM" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Active" name="is_active" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Description" name="description">
                <TextArea rows={2} />
              </Form.Item>
            </Col>

            {/* ── Embedded PM schedule (same API fields as backend PMPlan) ── */}
            <Col span={24}>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>PM Schedule</div>
            </Col>
            <Col span={12}>
              <Form.Item label="Cycle Type" name="cycle_type" rules={[{ required: true }]}>
                <Select
                  placeholder="Select cycle type"
                  onChange={(v) => {
                    setCycleType(v);
                    if (v === "monthly") {
                      const sd = form.getFieldValue("start_date");
                      const patch = {
                        monthly_interval: form.getFieldValue("monthly_interval") ?? 1,
                        monthly_pattern: "day_of_month",
                      };
                      const dom = dayOfMonthFromStartDate(sd ? String(sd) : "");
                      if (dom != null) patch.monthly_day_of_month = dom;
                      form.setFieldsValue(patch);
                    } else {
                      form.setFieldsValue({
                        monthly_interval: 1,
                        monthly_pattern: "day_of_month",
                        monthly_day_of_month: undefined,
                        monthly_weekday: undefined,
                        monthly_week_ordinal: undefined,
                      });
                    }
                  }}
                >
                  {CYCLE_TYPES.map((t) => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {cycleType === "monthly" && (
              <>
                <Col span={12}>
                  <Form.Item
                    label="Repeat every (months)"
                    name="monthly_interval"
                    rules={[{ required: true, type: "number", min: 1 }]}
                  >
                    <InputNumber min={1} precision={0} style={{ width: "100%" }} placeholder="1" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Monthly pattern" name="monthly_pattern" rules={[{ required: true }]}>
                    <Select
                      onChange={(v) => {
                        if (v === "day_of_month") {
                          form.setFieldsValue({
                            monthly_weekday: undefined,
                            monthly_week_ordinal: undefined,
                          });
                          const sd = form.getFieldValue("start_date");
                          const dom = dayOfMonthFromStartDate(sd ? String(sd) : "");
                          if (dom != null) form.setFieldsValue({ monthly_day_of_month: dom });
                        } else if (v === "nth_weekday") {
                          form.setFieldsValue({ monthly_day_of_month: undefined });
                        }
                      }}
                    >
                      {MONTHLY_PATTERN_OPTIONS.map((p) => (
                        <Option key={p.value} value={p.value}>{p.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.monthly_pattern !== cur.monthly_pattern}>
                  {({ getFieldValue }) => {
                    const pat = getFieldValue("monthly_pattern");
                    return (
                      <>
                        {pat === "day_of_month" && (
                          <Col span={12}>
                            <Form.Item
                              label="Day of month (1–31)"
                              name="monthly_day_of_month"
                              rules={[
                                { required: true, message: "Enter day of month" },
                                { type: "number", min: 1, max: 31 },
                              ]}
                            >
                              <InputNumber min={1} max={31} precision={0} style={{ width: "100%" }} />
                            </Form.Item>
                          </Col>
                        )}
                        {pat === "nth_weekday" && (
                          <>
                            <Col span={12}>
                              <Form.Item
                                label="Week ordinal in month"
                                name="monthly_week_ordinal"
                                rules={[{ required: true, message: "Select ordinal" }]}
                              >
                                <Select placeholder="e.g. Fourth">
                                  {MONTHLY_ORDINAL_OPTIONS.map((o) => (
                                    <Option key={o.value} value={o.value}>{o.label}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Weekday"
                                name="monthly_weekday"
                                rules={[{ required: true, message: "Select weekday" }]}
                              >
                                <Select placeholder="e.g. Wednesday">
                                  {WEEKDAY_OPTIONS.map((w) => (
                                    <Option key={w.value} value={w.value}>{w.label}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                          </>
                        )}
                      </>
                    );
                  }}
                </Form.Item>
                <Col span={24}>
                  <Text type="secondary">
                    Short months clamp to the last day. Weekdays: Monday = 0 … Sunday = 6 (API).
                  </Text>
                </Col>
              </>
            )}

            {(cycleType === "every_n_days" || cycleType === "every_n_hours") && (
              <Col span={12}>
                <Form.Item
                  label={cycleType === "every_n_days" ? "Number of Days" : "Number of Operating Hours"}
                  name="cycle_value"
                  rules={[{ required: true, message: "Please enter cycle value" }]}
                >
                  <Input
                    type="number"
                    min={1}
                    placeholder={cycleType === "every_n_days" ? "e.g. 90" : "e.g. 500"}
                    addonAfter={cycleType === "every_n_days" ? "days" : "hours"}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={12}>
              <Form.Item label="Schedule Start Date" name="start_date" rules={[{ required: true }]}>
                <CustomDatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Schedule End Date (optional)" name="end_date">
                <CustomDatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="skip_non_business_days" valuePropName="checked">
                <Checkbox>
                  Skip weekends &amp; holidays — if the due date falls on a non-business day, use the next business day
                </Checkbox>
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate={(p, c) => p.skip_non_business_days !== c.skip_non_business_days}>
              {({ getFieldValue }) =>
                getFieldValue("skip_non_business_days") ? (
                  <Col span={12}>
                    <Form.Item label="Public holiday calendar" name="holiday_country">
                      <Select allowClear placeholder="Weekends only">
                        {HOLIDAY_COUNTRY_OPTIONS.map((o) => (
                          <Option key={o.value || "none"} value={o.value}>{o.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                ) : null}
            </Form.Item>

            {!isAdd && (
              <Col span={12}>
                <Form.Item label="Next schedule date (read-only)" name="next_schedule_date">
                  <Input disabled placeholder="Calculated by system" />
                </Form.Item>
              </Col>
            )}

            <Col span={6}>
              <Form.Item label="Cost Budget ($)" name="cost_budget">
                <InputNumber min={0} precision={2} style={{ width: "100%" }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Estimated Total Hours" name="estimate_total_hours">
                <InputNumber min={0} precision={1} style={{ width: "100%" }} placeholder="0.0" addonAfter="hrs" />
              </Form.Item>
            </Col>

            {/* ── Step 1: Equipment Category filter ── */}
            <Col span={24}>
              <Form.Item
                label={
                  <span>
                    <strong>Step 1 —</strong> Equipment Category
                    <Tag color="blue" style={{ marginLeft: 8 }}>Filter</Tag>
                  </span>
                }
                name="equipment_category_filter"
                extra="Select a category to filter the equipment list and load matching PM Checklists."
              >
                <Select
                  allowClear
                  placeholder="Select equipment category…"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleEquipmentCategoryChange}
                >
                  {categoryOptions.map((c) => (
                    <Option key={c.id} value={c.id}>{c.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* ── Step 2: Equipment multi-select ── */}
            <Col span={24}>
              <Form.Item
                label={
                  <span>
                    <strong>Step 2 —</strong> Equipment
                    <Tag color={selectedEquipments.length ? "green" : "default"} style={{ marginLeft: 8 }}>
                      {selectedEquipments.length} selected
                    </Tag>
                  </span>
                }
                name="equipments"
                extra={
                  noCategorySelected
                    ? "You may select a category above to narrow the list, or search all equipment directly."
                    : undefined
                }
              >
                <Select
                  mode="multiple"
                  placeholder="Search and select equipment…"
                  filterOption={false}
                  onSearch={debouncedSearch}
                  loading={equipmentSearchLoading}
                  notFoundContent={equipmentSearchLoading ? "Searching…" : "No equipment found"}
                  onChange={(vals) => setSelectedEquipments(vals)}
                  showSearch
                  style={{ width: "100%" }}
                >
                  {equipmentOptions.map((eq) => (
                    <Option key={eq.id} value={eq.id}>
                      {eq.name}
                      {eq.equipment_category_name && (
                        <Tag style={{ marginLeft: 6, fontSize: 11 }}>
                          {eq.equipment_category_name}
                        </Tag>
                      )}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* ── Step 3: PM Checklist ── */}
            <Col span={24}>
              <Form.Item
                label={
                  <span>
                    <strong>Step 3 —</strong> PM Checklist (optional)
                  </span>
                }
                name="pm_checklist"
                extra={
                  checklistDisabledInEdit
                    ? "Select an equipment category first to load available checklists."
                    : "If left blank, each workorder's checklist is auto-matched by the equipment's category."
                }
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  loading={checklistLoading}
                  placeholder={
                    checklistDisabledInEdit ? "Select category first…" : "Select PM checklist (optional)"
                  }
                  disabled={checklistDisabledInEdit}
                >
                  {checklistOptions.map((cl) => (
                    <Option key={cl.id} value={cl.id}>{cl.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default PMPlanDetail;
